import * as React from "react";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { trDts } from "@modules/datasheets/utils/i18n";

export type HeadingValue = {
  displayHeading: boolean;
  textValue: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  position: DatasheetFieldComponentHeadingPosition;
  align: DatasheetFieldComponentTextAlign;
  textStyles: Array<DatasheetComponentTextStyle>;
};

export type HeadingProps = {
  value: HeadingValue;
  onChange: (value: HeadingValue) => void;
};

export const useHeadingController = (props: HeadingProps) => {
  const { onChange } = props;

  const handleChange = React.useCallback((value: Partial<HeadingValue>) => {
    onChange({
      ...props.value,
      ...value,
    });
  }, [onChange, props.value]);

  const handleDisplayHeadingChange = React.useCallback((displayHeading: boolean) => {
    handleChange({ displayHeading });
  }, [handleChange]);

  const handleTextValueChange = React.useCallback((textValue: string) => {
    const textValueToStore = textValue || ''; 
    if (textValue && textValue.trim() !== '' && !props.value.displayHeading) {
      handleChange({ textValue: textValueToStore, displayHeading: true });
    } else {
      handleChange({ textValue: textValueToStore });
    }
  }, [handleChange, props.value.displayHeading]);

  const handleFontFamilyChange = React.useCallback((fontFamily: string) => {
    handleChange({ fontFamily });
  }, [handleChange]);

  const handleFontSizeChange = React.useCallback((fontSize: number) => {
    handleChange({ fontSize });
  }, [handleChange]);

  const handleColorChange = React.useCallback((color: string) => {
    handleChange({ color });
  }, [handleChange]);

  const handlePositionChange = React.useCallback((position: DatasheetFieldComponentHeadingPosition) => {
    handleChange({ position });
  }, [handleChange]);

  const handleAlignChange = React.useCallback((align: DatasheetFieldComponentTextAlign) => {
    handleChange({ align });
  }, [handleChange]);

  const handleStylesChange = React.useCallback((textStyles: Array<DatasheetComponentTextStyle>) => {
    handleChange({ textStyles });
  }, [handleChange]);

  const positions = React.useMemo(
    () => [
      {
        value: DatasheetFieldComponentHeadingPosition.top,
        label: trDts("Top"),
      },
      {
        value: DatasheetFieldComponentHeadingPosition.left,
        label: trDts("Left"),
      },
      {
        value: DatasheetFieldComponentHeadingPosition.bottom,
        label: trDts("Bottom"),
      },
      {
        value: DatasheetFieldComponentHeadingPosition.right,
        label: trDts("Right"),
      },
    ],
    []
  );

  return {
    positions,
    handleDisplayHeadingChange,
    handleTextValueChange,
    handleFontFamilyChange,
    handleFontSizeChange,
    handleColorChange,
    handlePositionChange,
    handleAlignChange,
    handleStylesChange,
  };
};